import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Plus, Minus, ShoppingBag, ArrowLeft, Truck, Tag, ChevronRight, Star, ShoppingCart, X } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import { useCart } from "../context/CartContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { getProducts, validateCoupon } from "../lib/api";
import { toast } from "sonner";
import { resolveImageUrl, PLACEHOLDER_IMAGE } from "../utils/imageUrl";
import { CartAccessories } from "../components/Accessories";

const SHIPPING_METHODS = [
    { id: "ppl", label: "PPL Balík", desc: "Doručení 1–2 prac. dny", price: 199 },
    { id: "zasilkovna", label: "Zásilkovna", desc: "Doručení 2–3 prac. dny", price: 89 },
    { id: "express", label: "PPL Express", desc: "Doručení do 24h", price: 349 },
    { id: "personal", label: "Osobní odběr", desc: "Kladno – zdarma", price: 0 },
];

export default function CartPage() {
    const { items, removeItem, updateQuantity, subtotal, clearCart } = useCart();
    const navigate = useNavigate();
    const [shipping, setShipping] = useState("ppl");
    const [couponCode, setCouponCode] = useState("");
    const [coupon, setCoupon] = useState(null);
    const [couponLoading, setCouponLoading] = useState(false);
    const [related, setRelated] = useState([]);

    const shippingCost = SHIPPING_METHODS.find(s => s.id === shipping)?.price ?? 199;
    const discount = coupon ? Math.round(subtotal * coupon.value / 100) : 0;
    const total = subtotal + shippingCost - discount;
    const vatAmount = Math.round(total * 0.21 / 1.21);

    useEffect(() => {
        if (items.length > 0) {
            getProducts({ limit: 4, is_active: true, sort: "created_at_desc" })
                .then(r => setRelated((r.data?.items || []).filter(p => !items.find(i => i.product_id === p.id)).slice(0, 4)))
                .catch(() => {});
        }
    }, [items.length]);

    const handleCoupon = async () => {
        if (!couponCode.trim()) return;
        setCouponLoading(true);
        try {
            const res = await validateCoupon(couponCode.trim());
            setCoupon(res.data);
            toast.success(`Kupón "${res.data.name}" uplatněn – sleva ${res.data.value}%`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Neplatný kupón");
            setCoupon(null);
        } finally {
            setCouponLoading(false);
        }
    };

    if (items.length === 0) {
        return (
            <div className="min-h-screen bg-gray-50">
                <Navbar />
                <div className="max-w-2xl mx-auto px-4 py-24 text-center">
                    <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <ShoppingBag size={40} className="text-gray-300" />
                    </div>
                    <h2 className="text-2xl font-heading font-bold text-gray-800 mb-2">Váš košík je prázdný</h2>
                    <p className="text-gray-500 mb-8">Přidejte produkty ze širokého sortimentu Solar-Green</p>
                    <Button asChild style={{ backgroundColor: "var(--sg-primary)" }} className="text-white rounded-full px-8" data-testid="cart-continue-shopping">
                        <Link to="/kategorie">Pokračovat v nákupu</Link>
                    </Button>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />
            <div className="sg-container py-8">
                <div className="flex items-center gap-3 mb-6">
                    <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                        <ArrowLeft size={18} />
                    </button>
                    <h1 className="text-2xl font-heading font-bold text-gray-900">Košík</h1>
                    <span className="text-sm text-gray-400">({items.reduce((s,i) => s + i.quantity, 0)} položek)</span>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Items */}
                    <div className="lg:col-span-2 space-y-3">
                        {items.map(item => (
                            <div key={item.product_id} className="bg-white rounded-xl border border-gray-100 p-4 flex gap-4 hover:border-green-200 transition-colors" data-testid={`cart-item-${item.product_id}`}>
                                <Link to={`/produkt/${item.product_id}`} className="flex-shrink-0">
                                    <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-50 border border-gray-100">
                                        <img src={resolveImageUrl(item.image) || PLACEHOLDER_IMAGE} alt={item.name}
                                            className="w-full h-full object-contain p-1"
                                            onError={e => { e.target.src = PLACEHOLDER_IMAGE; e.target.onerror = null; }} />
                                    </div>
                                </Link>
                                <div className="flex-1 min-w-0">
                                    <Link to={`/produkt/${item.product_id}`} className="font-semibold text-gray-800 hover:text-green-700 text-sm line-clamp-2 block mb-0.5">
                                        {item.name}
                                    </Link>
                                    {item.sku && <p className="text-xs text-gray-400 mb-2">SKU: {item.sku}</p>}
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                                            <button onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                                                className="w-8 h-8 flex items-center justify-center hover:bg-gray-50 text-gray-500"
                                                data-testid={`cart-minus-${item.product_id}`}>
                                                <Minus size={12} />
                                            </button>
                                            <span className="w-10 text-center text-sm font-semibold" data-testid={`cart-qty-${item.product_id}`}>{item.quantity}</span>
                                            <button onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                                                className="w-8 h-8 flex items-center justify-center hover:bg-gray-50 text-gray-500"
                                                data-testid={`cart-plus-${item.product_id}`}>
                                                <Plus size={12} />
                                            </button>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="text-right">
                                                <span className="font-bold text-gray-900 text-sm block">
                                                    {(item.price * item.quantity).toLocaleString("cs-CZ")} Kč
                                                </span>
                                                {item.special_price && item.original_price && item.original_price > item.price && (
                                                    <span className="text-[10px] text-emerald-600 font-medium block">
                                                        Speciální cena
                                                        <span className="text-gray-400 line-through ml-1">{(item.original_price * item.quantity).toLocaleString("cs-CZ")} Kč</span>
                                                    </span>
                                                )}
                                                {!item.special_price && item.original_price && item.original_price > item.price && (
                                                    <span className="text-[10px] text-red-500 font-medium block">
                                                        Sleva
                                                        <span className="text-gray-400 line-through ml-1">{(item.original_price * item.quantity).toLocaleString("cs-CZ")} Kč</span>
                                                    </span>
                                                )}
                                                {item.quantity > 1 && (
                                                    <span className="text-xs text-gray-400">{item.price.toLocaleString("cs-CZ")} Kč/ks</span>
                                                )}
                                            </div>
                                            <button onClick={() => removeItem(item.product_id)}
                                                className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                data-testid={`cart-remove-${item.product_id}`}>
                                                <Trash2 size={15} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}

                        {/* Shipping selector */}
                        <div className="bg-white rounded-xl border border-gray-100 p-5">
                            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                <Truck size={16} style={{ color: "var(--sg-primary)" }} /> Doprava
                            </h3>
                            <div className="grid grid-cols-2 gap-2">
                                {SHIPPING_METHODS.map(m => (
                                    <label key={m.id} className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${shipping === m.id ? "border-green-500 bg-green-50" : "border-gray-100 hover:border-gray-200"}`}>
                                        <input type="radio" name="shipping" value={m.id} checked={shipping === m.id}
                                            onChange={() => setShipping(m.id)} className="hidden" data-testid={`cart-shipping-${m.id}`} />
                                        <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${shipping === m.id ? "border-green-500 bg-green-500" : "border-gray-300"}`} />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-gray-800">{m.label}</p>
                                            <p className="text-xs text-gray-400">{m.desc}</p>
                                        </div>
                                        <span className="text-sm font-bold text-gray-800 flex-shrink-0">
                                            {m.price === 0 ? <span className="text-green-600">Zdarma</span> : `${m.price} Kč`}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Coupon */}
                        <div className="bg-white rounded-xl border border-gray-100 p-5">
                            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                <Tag size={16} style={{ color: "var(--sg-primary)" }} /> Slevový kupón
                            </h3>
                            {coupon ? (
                                <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl p-3">
                                    <Tag size={16} className="text-green-600" />
                                    <div className="flex-1">
                                        <p className="font-semibold text-green-800 text-sm">{coupon.name}</p>
                                        <p className="text-xs text-green-600">Sleva {coupon.value}% = −{discount.toLocaleString("cs-CZ")} Kč</p>
                                    </div>
                                    <button onClick={() => { setCoupon(null); setCouponCode(""); }} className="p-1 text-green-600 hover:text-green-800">
                                        <X size={14} />
                                    </button>
                                </div>
                            ) : (
                                <div className="flex gap-2">
                                    <Input value={couponCode} onChange={e => setCouponCode(e.target.value.toUpperCase())}
                                        placeholder="Zadejte kód kupónu..." className="flex-1 h-10 text-sm tracking-widest font-mono"
                                        data-testid="coupon-input" onKeyDown={e => e.key === "Enter" && handleCoupon()} />
                                    <Button onClick={handleCoupon} disabled={couponLoading || !couponCode.trim()}
                                        className="h-10 px-4 text-white" style={{ backgroundColor: "var(--sg-primary)" }}
                                        data-testid="coupon-apply-btn">
                                        {couponLoading ? "..." : "Uplatnit"}
                                    </Button>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-between items-center pt-2">
                            <button onClick={clearCart} className="text-sm text-red-500 hover:text-red-700 transition-colors">
                                Vymazat košík
                            </button>
                            <Link to="/kategorie" className="text-sm font-semibold hover:underline" style={{ color: "var(--sg-primary)" }}>
                                ← Pokračovat v nákupu
                            </Link>
                        </div>
                    </div>

                    {/* Summary */}
                    <div>
                        <div className="bg-white rounded-xl border border-gray-100 p-6 sticky top-20">
                            <h2 className="font-semibold text-gray-900 mb-5 text-lg">Shrnutí objednávky</h2>
                            <div className="space-y-3 text-sm">
                                <div className="flex justify-between text-gray-600">
                                    <span>Mezisoučet ({items.reduce((s,i)=>s+i.quantity,0)} ks)</span>
                                    <span>{subtotal.toLocaleString("cs-CZ")} Kč</span>
                                </div>
                                <div className="flex justify-between text-gray-600">
                                    <span>Doprava ({SHIPPING_METHODS.find(s => s.id === shipping)?.label})</span>
                                    <span className={shippingCost === 0 ? "text-green-600 font-semibold" : ""}>
                                        {shippingCost === 0 ? "Zdarma" : `${shippingCost} Kč`}
                                    </span>
                                </div>
                                {coupon && (
                                    <div className="flex justify-between text-green-600 font-semibold">
                                        <span>Sleva ({coupon.name})</span>
                                        <span>−{discount.toLocaleString("cs-CZ")} Kč</span>
                                    </div>
                                )}
                                {shippingCost > 0 && subtotal < 5000 && (
                                    <p className="text-xs text-gray-400 bg-blue-50 border border-blue-100 rounded-lg p-2 text-center">
                                        Přidejte zboží za <strong>{(5000 - subtotal).toLocaleString("cs-CZ")} Kč</strong> pro dopravu zdarma PPL
                                    </p>
                                )}
                                <div className="flex justify-between text-xs text-gray-400 pt-1">
                                    <span>z toho DPH 21%</span>
                                    <span>{vatAmount.toLocaleString("cs-CZ")} Kč</span>
                                </div>
                            </div>
                            <div className="flex justify-between items-end pt-4 mt-4 border-t border-gray-100">
                                <span className="font-bold text-gray-900 text-base">Celkem s DPH</span>
                                <span className="text-2xl font-bold" style={{ color: "var(--sg-text)" }} data-testid="cart-total">
                                    {total.toLocaleString("cs-CZ")} Kč
                                </span>
                            </div>
                            <Button asChild className="w-full mt-5 text-white h-12 rounded-xl font-semibold text-base"
                                style={{ backgroundColor: "var(--sg-primary)" }} data-testid="cart-checkout-btn">
                                <Link to={`/pokladna?shipping=${shipping}${coupon ? `&coupon=${couponCode}&discount=${discount}` : ""}`}>
                                    Pokračovat k pokladně <ChevronRight size={16} />
                                </Link>
                            </Button>
                            <div className="mt-4 pt-4 border-t border-gray-100">
                                <p className="text-xs text-gray-400 text-center mb-2">Bezpecna platba</p>
                                <div className="flex gap-2 justify-center flex-wrap">
                                    {["Karta", "Dobirka", "Prevodem", "Zasilkovna"].map(m => (
                                        <span key={m} className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded">{m}</span>
                                    ))}
                                </div>
                            </div>

                            {/* Cart accessories recommendations */}
                            <CartAccessories cartItems={items} />
                        </div>
                    </div>
                </div>

                {/* Cross-sell */}
                {related.length > 0 && (
                    <div className="mt-12">
                        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <Star size={18} style={{ color: "var(--sg-primary)" }} /> Mohlo by vás zajímat
                        </h2>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {related.map(p => <ProductCard key={p.id} product={p} />)}
                        </div>
                    </div>
                )}
            </div>
            <Footer />
        </div>
    );
}
